import React from 'react';

import { Trash2, X } from 'lucide-react';

import { Button } from './common';

interface Document {
	id: string;
	name: string;
	type: string;
	date: string;
	size: string;
	status: string;
	processingMonth: string;
	pdfUrl?: string;
	results?: any;
	uploaded_by?: { name: string; email: string };
}

interface DeleteConfirmationModalProps {
	isOpen: boolean;
	document: Document | null;
	isDeleting: boolean;
	errorMessage?: string | null;
	onConfirm: () => void;
	onCancel: () => void;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
	isOpen,
	document,
	isDeleting,
	errorMessage,
	onConfirm,
	onCancel,
}) => {
	if (!isOpen || !document) {
		return null;
	}

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 font-primary">
				{/* Modal Header */}
				<div className="flex items-center justify-between p-6 border-b border-neutral-200">
					<div className="flex items-center">
						<div className="flex-shrink-0 w-10 h-10 bg-error-100 rounded-full flex items-center justify-center">
							<Trash2 className="w-5 h-5 text-error-600" />
						</div>
						<div className="ml-4">
							<h3 className="text-lg font-semibold text-neutral-900 font-primary">
								Delete Document
							</h3>
							<p className="text-sm text-neutral-500 font-primary">
								This action cannot be undone
							</p>
						</div>
					</div>
					<button
						onClick={onCancel}
						className="text-neutral-400 hover:text-neutral-600 transition-colors"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				{/* Modal Body */}
				<div className="p-6">
					<p className="text-neutral-700 font-primary">
						Are you sure you want to delete{' '}
						<span className="font-semibold text-neutral-900">
							"{document.name}"
						</span>
						? This will permanently remove the document and all its analysis
						data.
					</p>
					{errorMessage && (
						<div className="mt-4 bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700 font-primary">
							{errorMessage}
						</div>
					)}
				</div>

				{/* Modal Footer */}
				<div className="flex space-x-3 p-6 bg-neutral-50 rounded-b-xl">
					<Button
						onClick={onCancel}
						disabled={isDeleting}
						className="flex-1 px-4 py-3 text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-primary"
					>
						Cancel
					</Button>
					<Button
						onClick={onConfirm}
						disabled={isDeleting}
						variant="danger"
						className="flex-1 px-4 py-3 bg-gradient-to-r from-error-600 to-error-700 text-white rounded-lg hover:from-error-700 hover:to-error-800 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed font-primary"
					>
						{isDeleting ? (
							<>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
								Deleting...
							</>
						) : (
							'Delete Document'
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default DeleteConfirmationModal;
